import { useEffect, useState } from "react";
import { ClipboardList, FileDown, Loader2, RotateCcw } from "lucide-react";

async function openDiscountedPdf() {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  const res = await fetch("/api/discounted/generate", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error("Chyba při generování PDF");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function downloadDiscountedCsv() {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  const res = await fetch("/api/discounted/csv", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    throw new Error(json?.error || "Chyba při stahování CSV");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "zlevneni_ohrozencu.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function DiscountedPage() {
  const defaultRules = [
    { days: 90, discount: 15, mode: "z_prodejni" },
    { days: 60, discount: 0, mode: "za_nakupku" },
    { days: 30, discount: 50, mode: "z_prodejni" },
  ];

  const [rules, setRules] = useState(defaultRules);
  const [loading, setLoading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const token =
          localStorage.getItem("token") || sessionStorage.getItem("token") || "";
        const res = await fetch("/api/discounted/rules", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json?.rule?.rules) setRules(json.rule.rules);
      } catch {}
    })();
  }, []);

  const handleChange = (i, field, val) => {
    const next = [...rules];
    next[i][field] = field === "discount" ? Number(val) : val;
    setRules(next);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token") || "";
      const res = await fetch("/api/discounted/rules", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rules }),
      });
      const json = await res.json();
      if (json?.ok) setMessage("✅ Pravidla uložena.");
      else setMessage("⚠️ Nelze uložit.");
    } catch {
      setMessage("❌ Chyba při ukládání.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setRules(defaultRules);
    setMessage("⚙️ Pravidla byla resetována.");
    await handleSave();
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      await openDiscountedPdf();
    } catch (e) {
      setMessage(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      setDownloadingCsv(true);
      await downloadDiscountedCsv();
    } catch (e) {
      setMessage(e.message);
    } finally {
      setDownloadingCsv(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-600 text-white p-3 rounded-xl">
          <ClipboardList size={22} />
        </div>
        <h1 className="text-2xl font-semibold">
          Zlevnění volně prodejných ohroženců
        </h1>
      </div>

      <div className="bg-white shadow-sm rounded-xl border border-gray-200 p-6">
        <div className="font-semibold text-gray-800 mb-4">Nastavení slev</div>

        <div className="grid gap-5 mb-6">
          {rules.map((r, i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex justify-between items-center mb-2">
                <strong>Pod {r.days} dní do expirace</strong>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="font-medium">Zlevnit o:</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={r.discount}
                  disabled={r.mode === "za_nakupku"}
                  onChange={(e) => handleChange(i, "discount", e.target.value)}
                  className={`border rounded-md px-2 py-1 w-20 text-center ${
                    r.mode === "za_nakupku"
                      ? "bg-gray-100 cursor-not-allowed"
                      : ""
                  }`}
                />
                <span>%</span>

                <div className="flex gap-4 ml-4">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`mode-${i}`}
                      value="z_prodejni"
                      checked={r.mode === "z_prodejni"}
                      onChange={(e) => handleChange(i, "mode", e.target.value)}
                    />
                    <span>z prodejní ceny</span>
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name={`mode-${i}`}
                      value="za_nakupku"
                      checked={r.mode === "za_nakupku"}
                      onChange={(e) => handleChange(i, "mode", e.target.value)}
                    />
                    <span>za nákupní cenu</span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-gray-100 hover:bg-gray-200 font-semibold"
          >
            {saving ? "Ukládám..." : "💾 Uložit nastavení"}
          </button>

          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-gray-100 hover:bg-gray-200 font-semibold flex items-center gap-1"
          >
            <RotateCcw size={16} /> Resetovat
          </button>

          <button
            onClick={handleGenerate}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 flex items-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
            Vygenerovat PDF
          </button>

          <button
            onClick={handleDownloadCsv}
            disabled={downloadingCsv}
            className="px-4 py-2 rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2"
          >
            {downloadingCsv ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
            Stáhnout CSV
          </button>
        </div>

        {message && (
          <div className="mt-3 text-sm text-gray-700">{message}</div>
        )}
      </div>

      {/* volný prostor pro doplňkový text (jako u letákové objednávky) */}
      <div className="mt-8 text-sm text-gray-600 leading-relaxed">
        <p>
          Tento přehled umožňuje automaticky stanovit doporučené prodejní ceny
          volně prodejných ohroženců dle jejich zbývající expirace.
        </p>
        <p>
          Výsledné PDF obsahuje seznam přípravků, jejichž cena se sníží podle
          nastavených pravidel. Sleva se může uplatnit procentuálně z
          prodejní ceny, nebo nastavit pevně na úroveň nákupní ceny.
        </p>
      </div>
    </div>
  );
}
